/**
 * TURNOS PAGE - Turn Management Module
 * Gestión de turnos del laboratorio
 *
 * Features:
 * - Generate new turns with priority
 * - Real-time queue panel
 * - Call, finish, cancel turns
 * - Statistics dashboard
 * - Print tickets
 * - My attention point selection
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import {
  CalendarClock,
  Plus,
  RefreshCw,
  Phone,
  PhoneCall,
  CheckCircle,
  XCircle,
  Printer,
  BarChart3,
  Users,
  AlertCircle,
  Monitor,
  ExternalLink,
  Tablet,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useIsMobile } from "@/components/ui/responsive-table";

import { useSucursal } from "@/contexts/sucursal-context";
import { useApiError } from "@/hooks/use-api-error";
import {
  usePanelTurnos,
  useCrearTurno,
  useLlamarTurno,
  useRellamarTurno,
  useUpdateTurnoPrioridad,
  useFinalizarTurno,
  useCancelarTurno,
  useEstadisticasTurnos,
  useTiempoEspera,
  usePuntosAtencionActivos,
  type Turno,
  type TurnoListItem,
} from "@/hooks/use-turnos";

import { TurnoFormDialog } from "./components/turno-form-dialog";
import { TicketTurno } from "./components/ticket-turno";
import { PanelTurnos } from "./components/panel-turnos";
import { EstadisticasTurnos } from "./components/estadisticas-turnos";

export default function TurnosPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva } = useSucursal();
  const isMobile = useIsMobile();

  // State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"llamar" | "finalizar" | "cancelar" | null>(
    null
  );
  const [selectedTurno, setSelectedTurno] = useState<TurnoListItem | null>(null);
  const [selectedPuntoAtencion, setSelectedPuntoAtencion] = useState<number | null>(null);
  const [createdTurno, setCreatedTurno] = useState<Turno | null>(null);
  const [activeTab, setActiveTab] = useState("panel");

  // Mi Punto de Atención - persisted in localStorage
  const [miPuntoAtencion, setMiPuntoAtencion] = useState<number | null>(() => {
    const saved = localStorage.getItem(`miPuntoAtencion_${sucursalActiva?.id}`);
    return saved ? Number(saved) : null;
  });

  // Update miPuntoAtencion when sucursal changes
  useEffect(() => {
    if (sucursalActiva?.id) {
      const saved = localStorage.getItem(`miPuntoAtencion_${sucursalActiva.id}`);
      setMiPuntoAtencion(saved ? Number(saved) : null);
    }
  }, [sucursalActiva?.id]);

  // Save miPuntoAtencion to localStorage
  const handleSetMiPuntoAtencion = useCallback(
    (puntoId: number | null) => {
      setMiPuntoAtencion(puntoId);
      if (sucursalActiva?.id) {
        if (puntoId) {
          localStorage.setItem(`miPuntoAtencion_${sucursalActiva.id}`, String(puntoId));
        } else {
          localStorage.removeItem(`miPuntoAtencion_${sucursalActiva.id}`);
        }
      }
    },
    [sucursalActiva?.id]
  );

  // Print ref
  const ticketRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: ticketRef,
    documentTitle: `Turno-${createdTurno?.numero || ""}`,
  });

  // Queries
  const sucursalId = sucursalActiva?.id || 0;

  const {
    data: turnos = [],
    isLoading: isLoadingTurnos,
    error: turnosError,
    refetch: refetchTurnos,
  } = usePanelTurnos(sucursalId, undefined, {
    enabled: sucursalId > 0,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const { data: puntosAtencion = [] } = usePuntosAtencionActivos(sucursalId, {
    enabled: sucursalId > 0,
  });

  const { data: estadisticas, isLoading: isLoadingStats } = useEstadisticasTurnos(
    sucursalId,
    undefined,
    undefined,
    { enabled: sucursalId > 0 && activeTab === "estadisticas" }
  );

  const { data: tiempoEspera } = useTiempoEspera(sucursalId, undefined, {
    enabled: sucursalId > 0,
  });

  // Find current turn (in attention)
  const turnoActual = turnos.find((t) => t.estado === "EN_ATENCION") || null;

  // Find my punto de atencion object
  const miPuntoAtencionObj = puntosAtencion.find((p) => p.id === miPuntoAtencion) || null;

  // Mutations
  const crearTurnoMutation = useCrearTurno({
    onSuccess: (turno) => {
      setCreatedTurno(turno);
      setIsFormOpen(false);
      toast.success(
        t("turnos.toast.created", {
          numero: String(turno.numero).padStart(3, "0"),
        })
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const llamarTurnoMutation = useLlamarTurno({
    onSuccess: (turno) => {
      toast.success(
        t("turnos.toast.called", {
          numero: String(turno.numero).padStart(3, "0"),
        })
      );
      setIsConfirmOpen(false);
      setSelectedTurno(null);
      setSelectedPuntoAtencion(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const finalizarTurnoMutation = useFinalizarTurno({
    onSuccess: (turno) => {
      toast.success(
        t("turnos.toast.finished", {
          numero: String(turno.numero).padStart(3, "0"),
        })
      );
      setIsConfirmOpen(false);
      setSelectedTurno(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const cancelarTurnoMutation = useCancelarTurno({
    onSuccess: (turno) => {
      toast.warning(
        t("turnos.toast.cancelled", {
          numero: String(turno.numero).padStart(3, "0"),
        })
      );
      setIsConfirmOpen(false);
      setSelectedTurno(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const rellamarTurnoMutation = useRellamarTurno({
    onSuccess: (turno) => {
      toast.info(
        t("turnos.toast.recalled", "Turno {{numero}} rellamado ({{veces}} veces)", {
          numero: String(turno.numero).padStart(3, "0"),
          veces: turno.veces_llamado,
        })
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updatePrioridadMutation = useUpdateTurnoPrioridad({
    onSuccess: (turno) => {
      toast.success(
        t(
          "turnos.toast.priorityUpdated",
          "Prioridad del turno {{numero}} actualizada a {{prioridad}}",
          {
            numero: String(turno.numero).padStart(3, "0"),
            prioridad: turno.prioridad_display,
          }
        )
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleCreateTurno = useCallback(
    (data: { prioridad: string; tipo_servicio?: string; observaciones?: string }) => {
      if (!sucursalId) return;
      crearTurnoMutation.mutate({
        sucursal_id: sucursalId,
        prioridad: data.prioridad as "BAJA" | "MEDIA" | "ALTA",
        tipo_servicio: data.tipo_servicio,
        observaciones: data.observaciones,
      });
    },
    [sucursalId, crearTurnoMutation]
  );

  const handleLlamarSiguiente = useCallback(() => {
    const siguiente = turnos.find((t) => t.estado === "ESPERANDO");
    if (!siguiente) {
      toast.info(t("turnos.noWaiting", "No hay turnos en espera"));
      return;
    }

    // If user has "Mi Punto de Atención" set, use it directly
    if (miPuntoAtencion) {
      setSelectedTurno(siguiente);
      setSelectedPuntoAtencion(miPuntoAtencion);
      setConfirmAction("llamar");
      setIsConfirmOpen(true);
    } else if (puntosAtencion.length > 0) {
      // Otherwise, show dialog to select punto de atención
      setSelectedTurno(siguiente);
      setSelectedPuntoAtencion(puntosAtencion[0].id);
      setConfirmAction("llamar");
      setIsConfirmOpen(true);
    } else {
      toast.error(t("turnos.noPuntosAtencion", "No hay puntos de atención disponibles"));
    }
  }, [turnos, puntosAtencion, miPuntoAtencion, t]);

  const handleLlamarTurnoEspecifico = useCallback(
    (turno: TurnoListItem) => {
      if (miPuntoAtencion) {
        setSelectedTurno(turno);
        setSelectedPuntoAtencion(miPuntoAtencion);
        setConfirmAction("llamar");
        setIsConfirmOpen(true);
      } else if (puntosAtencion.length > 0) {
        setSelectedTurno(turno);
        setSelectedPuntoAtencion(puntosAtencion[0].id);
        setConfirmAction("llamar");
        setIsConfirmOpen(true);
      } else {
        toast.error(t("turnos.noPuntosAtencion", "No hay puntos de atención disponibles"));
      }
    },
    [puntosAtencion, miPuntoAtencion, t]
  );

  const handleFinalizarActual = useCallback(() => {
    if (turnoActual) {
      setSelectedTurno(turnoActual);
      setConfirmAction("finalizar");
      setIsConfirmOpen(true);
    }
  }, [turnoActual]);

  const handleCancelarTurno = useCallback((turno: TurnoListItem) => {
    setSelectedTurno(turno);
    setConfirmAction("cancelar");
    setIsConfirmOpen(true);
  }, []);

  const handleRellamarTurno = useCallback(() => {
    if (turnoActual) {
      rellamarTurnoMutation.mutate(turnoActual.id);
    }
  }, [turnoActual, rellamarTurnoMutation]);

  const handleConfirmAction = useCallback(() => {
    if (!selectedTurno) return;

    if (confirmAction === "llamar" && selectedPuntoAtencion) {
      llamarTurnoMutation.mutate({
        turnoId: selectedTurno.id,
        puntoAtencionId: selectedPuntoAtencion,
      });
    } else if (confirmAction === "finalizar") {
      finalizarTurnoMutation.mutate(selectedTurno.id);
    } else if (confirmAction === "cancelar") {
      cancelarTurnoMutation.mutate({ turnoId: selectedTurno.id });
    }
  }, [
    selectedTurno,
    confirmAction,
    selectedPuntoAtencion,
    llamarTurnoMutation,
    finalizarTurnoMutation,
    cancelarTurnoMutation,
  ]);

  // No branch selected
  if (!sucursalActiva) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4 p-4">
        <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground" />
        <h2 className="text-lg sm:text-xl font-semibold text-center">
          {t("turnos.selectBranch", "Seleccione una sucursal")}
        </h2>
        <p className="text-muted-foreground text-sm sm:text-base text-center">
          {t("turnos.selectBranchDesc", "Debe seleccionar una sucursal para gestionar turnos")}
        </p>
      </div>
    );
  }

  const isLoading = isLoadingTurnos;
  const error = turnosError ? getErrorMessage(turnosError) : null;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <CalendarClock className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          <div>
            <h1 className="text-lg sm:text-2xl font-bold">{t("turnos.title")}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">{sucursalActiva.nombre}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (sucursalActiva?.codigo) {
                window.open(`/pantalla-turnos/${sucursalActiva.codigo}`, "_blank");
              }
            }}
            disabled={!sucursalActiva?.codigo}
            title={t("turnos.openPublicDisplay", "Abrir pantalla pública")}
          >
            <ExternalLink className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {!isMobile && t("turnos.publicDisplay", "Pantalla")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (sucursalActiva?.codigo) {
                window.open(`/kiosko-turnos/${sucursalActiva.codigo}`, "_blank");
              }
            }}
            disabled={!sucursalActiva?.codigo}
            title={t("turnos.openKiosk", "Abrir kiosko de turnos")}
          >
            <Tablet className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {!isMobile && t("turnos.kiosk", "Kiosko")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetchTurnos()} disabled={isLoading}>
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size="sm" onClick={() => setIsFormOpen(true)}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("turnos.newTurno")}
          </Button>
        </div>
      </div>

      {/* Quick Stats - Responsive grid */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        {/* Mi Punto de Atención */}
        <Card
          className={`col-span-2 sm:col-span-1 ${miPuntoAtencion ? "border-primary" : "border-dashed"}`}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">
              {t("turnos.myAttentionPoint", "Mi Punto de Atención")}
            </CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <Select
              value={miPuntoAtencion?.toString() || "none"}
              onValueChange={(v) => handleSetMiPuntoAtencion(v === "none" ? null : Number(v))}
            >
              <SelectTrigger
                className={`text-sm ${!miPuntoAtencion ? "text-muted-foreground" : ""}`}
              >
                <SelectValue placeholder={t("turnos.selectMyPoint", "Seleccionar...")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("turnos.noPointSelected", "Sin asignar")}</SelectItem>
                {puntosAtencion.map((punto) => (
                  <SelectItem key={punto.id} value={punto.id.toString()}>
                    {punto.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {miPuntoAtencionObj && (
              <p className="text-xs text-muted-foreground mt-2 hidden sm:block">
                {t("turnos.pointType", "Tipo")}: {miPuntoAtencionObj.tipo}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">
              {t("turnos.waitingQueue", "Cola de Espera")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            {isLoading ? (
              <Skeleton className="h-6 sm:h-8 w-12 sm:w-16" />
            ) : (
              <div className="text-xl sm:text-2xl font-bold">
                {turnos.filter((t) => t.estado === "ESPERANDO").length}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">
              {t("turnos.estimatedWait", "Tiempo Estimado")}
            </CardTitle>
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold">
              {tiempoEspera ? `${tiempoEspera.tiempo_espera_minutos} min` : "-"}
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 sm:col-span-1">
          <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">
              {t("turnos.quickActions", "Acciones Rápidas")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-row sm:flex-col gap-2 p-3 pt-0 sm:p-6 sm:pt-0">
            <Button
              variant="default"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm"
              onClick={handleLlamarSiguiente}
              disabled={!turnos.some((t) => t.estado === "ESPERANDO") || !miPuntoAtencion}
              title={
                !miPuntoAtencion
                  ? t("turnos.selectPointFirst", "Seleccione su punto de atención primero")
                  : ""
              }
            >
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">{t("turnos.callNext", "Llamar Siguiente")}</span>
              <span className="xs:hidden">Llamar</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm"
              onClick={handleFinalizarActual}
              disabled={!turnoActual}
            >
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">
                {t("turnos.finishCurrent", "Finalizar Actual")}
              </span>
              <span className="xs:hidden">Finalizar</span>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger
            value="panel"
            className="flex-1 sm:flex-none flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <Users className="h-3 w-3 sm:h-4 sm:w-4" />
            {t("turnos.panel", "Panel")}
          </TabsTrigger>
          <TabsTrigger
            value="estadisticas"
            className="flex-1 sm:flex-none flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
            {t("turnos.statistics", "Estadísticas")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="panel" className="mt-3 sm:mt-4">
          <div className="grid gap-3 sm:gap-4 lg:grid-cols-3">
            {/* Queue Panel */}
            <div className="lg:col-span-2 order-1">
              <PanelTurnos
                turnos={turnos}
                turnoActual={turnoActual}
                isLoading={isLoading}
                error={error}
                onSelectTurno={handleLlamarTurnoEspecifico}
              />
            </div>

            {/* Actions Panel */}
            <div className="space-y-3 sm:space-y-4 order-2">
              {/* Current Turn Actions */}
              {turnoActual && (
                <Card>
                  <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-2">
                    <CardTitle className="text-base sm:text-lg flex items-center justify-between">
                      {t("turnos.currentTurnoActions", "Turno en Atención")}
                      {turnoActual.veces_llamado > 1 && (
                        <Badge variant="outline" className="text-xs">
                          <PhoneCall className="h-3 w-3 mr-1" />
                          {turnoActual.veces_llamado}x
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {t("turnos.turno", "Turno")} #{String(turnoActual.numero).padStart(3, "0")}
                      {turnoActual.punto_atencion_nombre && (
                        <span className="ml-2 text-muted-foreground">
                          • {turnoActual.punto_atencion_nombre}
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0 space-y-2">
                    <Button
                      className="w-full text-xs sm:text-sm"
                      size={isMobile ? "sm" : "default"}
                      variant="outline"
                      onClick={handleRellamarTurno}
                      disabled={rellamarTurnoMutation.isPending}
                    >
                      <PhoneCall className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      {t("turnos.recallTurno", "Rellamar")}
                      {turnoActual.veces_llamado > 1 && (
                        <span className="ml-1 text-muted-foreground">
                          ({turnoActual.veces_llamado})
                        </span>
                      )}
                    </Button>
                    <Button
                      className="w-full text-xs sm:text-sm"
                      size={isMobile ? "sm" : "default"}
                      variant="default"
                      onClick={handleFinalizarActual}
                    >
                      <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      {t("turnos.finishAttention", "Finalizar Atención")}
                    </Button>
                    <Button
                      className="w-full text-xs sm:text-sm"
                      size={isMobile ? "sm" : "default"}
                      variant="destructive"
                      onClick={() => handleCancelarTurno(turnoActual)}
                    >
                      <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      {t("turnos.cancelTurno", "Cancelar Turno")}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Waiting Turns List */}
              <Card>
                <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-2">
                  <CardTitle className="text-base sm:text-lg flex items-center justify-between">
                    {t("turnos.waitingList", "Turnos en Espera")}
                    <Badge variant="outline" className="text-xs">
                      {turnos.filter((t) => t.estado === "ESPERANDO").length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                  {isLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-10 sm:h-12 w-full" />
                      ))}
                    </div>
                  ) : turnos.filter((t) => t.estado === "ESPERANDO").length === 0 ? (
                    <p className="text-center text-muted-foreground py-3 sm:py-4 text-xs sm:text-sm">
                      {t("turnos.noWaiting", "No hay turnos en espera")}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[200px] sm:max-h-[300px] overflow-y-auto">
                      {turnos
                        .filter((t) => t.estado === "ESPERANDO")
                        .map((turno) => (
                          <div
                            key={turno.id}
                            className="flex items-center justify-between p-2 rounded border"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm sm:text-base">
                                {String(turno.numero).padStart(3, "0")}
                              </span>
                              <Select
                                value={turno.prioridad}
                                onValueChange={(value) =>
                                  updatePrioridadMutation.mutate({
                                    turnoId: turno.id,
                                    prioridad: value as "BAJA" | "MEDIA" | "ALTA",
                                  })
                                }
                              >
                                <SelectTrigger className="h-6 w-[90px] text-xs px-2 py-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="BAJA">
                                    <span className="text-xs">
                                      {t("turnos.priority.low", "Baja")}
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="MEDIA">
                                    <span className="text-xs">
                                      {t("turnos.priority.medium", "Media")}
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="ALTA">
                                    <span className="text-xs text-destructive font-semibold">
                                      {t("turnos.priority.high", "Alta")}
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelarTurno(turno)}
                            >
                              <XCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="estadisticas" className="mt-3 sm:mt-4">
          <EstadisticasTurnos estadisticas={estadisticas || null} isLoading={isLoadingStats} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <TurnoFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={handleCreateTurno}
        isLoading={crearTurnoMutation.isPending}
        sucursalNombre={sucursalActiva.nombre}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "llamar"
            ? t("turnos.confirmCall", "Llamar Turno")
            : confirmAction === "finalizar"
              ? t("turnos.confirmFinish", "Finalizar Turno")
              : t("turnos.confirmCancel", "Cancelar Turno")
        }
        description={
          confirmAction === "llamar" ? (
            <div className="space-y-4">
              <p>
                {t("turnos.confirmCallDesc", {
                  numero: selectedTurno ? String(selectedTurno.numero).padStart(3, "0") : "",
                })}
              </p>
              {miPuntoAtencionObj ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Monitor className="h-4 w-4" />
                  <span className="font-medium">{miPuntoAtencionObj.nombre}</span>
                </div>
              ) : puntosAtencion.length > 1 ? (
                <Select
                  value={selectedPuntoAtencion?.toString()}
                  onValueChange={(v) => setSelectedPuntoAtencion(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("turnos.selectPuntoAtencion", "Seleccione punto de atención")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {puntosAtencion.map((punto) => (
                      <SelectItem key={punto.id} value={punto.id.toString()}>
                        {punto.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          ) : confirmAction === "finalizar" ? (
            t("turnos.confirmFinishDesc", {
              numero: selectedTurno ? String(selectedTurno.numero).padStart(3, "0") : "",
            })
          ) : (
            t("turnos.confirmCancelDesc", {
              numero: selectedTurno ? String(selectedTurno.numero).padStart(3, "0") : "",
            })
          )
        }
        onConfirm={handleConfirmAction}
        variant={confirmAction === "cancelar" ? "destructive" : "default"}
        isLoading={
          llamarTurnoMutation.isPending ||
          finalizarTurnoMutation.isPending ||
          cancelarTurnoMutation.isPending
        }
      />

      {/* Print Ticket (hidden) */}
      {createdTurno && (
        <div className="hidden">
          <TicketTurno
            ref={ticketRef}
            turno={createdTurno}
            sucursalNombre={sucursalActiva.nombre}
          />
        </div>
      )}

      {/* Print Button after creating turn */}
      {createdTurno && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-50">
          <Card className="shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                <div className="text-center sm:text-left">
                  <p className="font-semibold text-sm sm:text-base">
                    {t("turnos.turnoCreated", "Turno Generado")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold">
                    #{String(createdTurno.numero).padStart(3, "0")}
                  </p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button size="sm" className="flex-1 sm:flex-none" onClick={() => handlePrint()}>
                    <Printer className="h-4 w-4 mr-1 sm:mr-2" />
                    {t("turnos.printTicket", "Imprimir")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 sm:flex-none"
                    onClick={() => setCreatedTurno(null)}
                  >
                    {t("common.close")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
